/**
 * UserStatusDot — Compact colored indicator for workflow status.
 *
 * - Renders a small colored circle (~8px) if `userStatus` is set.
 * - Returns `null` when no status is assigned.
 * - Click opens a Popover with UserStatusPicker.
 * - §21 spec: placed between TaskCheckbox and DateChip in compact row.
 */

import { memo, useState } from "react"
import { cn } from "@/shared/utils/general/cn"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/ui/popover"
import { UserStatusPicker } from "@/shared/components/UserStatusPicker"
import {
  getUserStatusDisplay,
  normalizeUserStatus,
  type CustomUserStatus,
} from "@/shared/constants/userStatus"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserStatusDotProps {
  /** Raw userStatus value from DB (number | string | undefined) */
  userStatus?: string | number | null
  /** The task/object ID to update */
  taskId: string
  /** Custom statuses from the container (project/list), if any */
  customStatuses?: CustomUserStatus[]
  className?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const UserStatusDot = memo(function UserStatusDot({
  userStatus,
  taskId,
  customStatuses,
  className,
}: UserStatusDotProps) {
  const [open, setOpen] = useState(false)

  const normalized = normalizeUserStatus(userStatus)
  if (!normalized) return null

  const { color, isActive } = getUserStatusDisplay(normalized, customStatuses)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex shrink-0 items-center p-1.5 -m-1 rounded cursor-pointer",
            "transition-colors",
            className,
          )}
          onClick={(e) => {
            e.stopPropagation()
            setOpen(true)
          }}
          aria-label="Change status"
        >
          <span
            className={cn(
              "block size-3  rounded-full",
              !isActive && "ring-1 ring-muted-foreground/30",
            )}
            style={{ backgroundColor: color }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="flex w-56 flex-col overflow-hidden p-1.5"
        onClick={(e) => e.stopPropagation()}
      >
        <UserStatusPicker
          taskId={taskId}
          currentValue={userStatus}
          customStatuses={customStatuses}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
})
